import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Star, Trash2 } from 'lucide-react'
import { listPhotos, deletePhoto, setMainPhoto, reportOnboardingStep, type Photo } from '../lib/api'
import { useUserStore } from '../store/userStore'
import { useTelegramBackButton } from '../lib/telegram'

// Cut from 4 to 3 (see docs/ui-references/ton-dating onboarding overhaul):
// 1 large slot (position 1, the main photo) + 2 small slots (positions 2-3).
// The DB still allows position 4 (migration 000003) — see slotsFromPhotos
// below for how a legacy 4th photo is handled without being hidden from its
// owner.
const MAX_PHOTOS = 3
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const COMPRESS_MAX_PX = 1200
const COMPRESS_QUALITY = 0.85

async function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      const scale = Math.min(1, COMPRESS_MAX_PX / Math.max(img.width, img.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(img.width * scale)
      canvas.height = Math.round(img.height * scale)
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Compression failed'))),
        'image/webp',
        COMPRESS_QUALITY,
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Image load failed'))
    }
    img.src = objectUrl
  })
}

interface PhotoSlot {
  id?: number
  position: number
  status?: 'pending' | 'approved' | 'rejected'
  url?: string
  is_main?: boolean
  localPreview?: string
  uploading?: boolean
  actioning?: boolean // deleting or setting main
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

async function authedFetch(path: string, opts: RequestInit = {}): Promise<Response> {
  const { getInitData } = await import('../lib/telegram')
  const initData = getInitData()
  return fetch(BASE_URL + path, {
    ...opts,
    headers: {
      ...(opts.headers ?? {}),
      Authorization: `tma ${initData}`,
    },
  })
}

/**
 * Splits the server's photo list into the 3 grid slots plus an `overflow`
 * bucket for anything beyond position MAX_PHOTOS (only possible for
 * accounts created before the grid was cut from 4 to 3 photos).
 *
 * Overflow photos are deliberately still returned (not dropped): the old
 * behavior here silently discarded any photo with position > MAX_PHOTOS,
 * which would have made a legacy 4th photo disappear from its owner's own
 * editor while ApprovedR2KeysByUser kept serving it to everyone else in the
 * feed — leaving the owner unable to remove a photo other people can see.
 * The overflow strip in the page below renders these as delete-only, with
 * no "+" slot to re-add into position 4.
 */
export function slotsFromPhotos(photos: Photo[]): { slots: PhotoSlot[]; overflow: PhotoSlot[] } {
  const slots: PhotoSlot[] = Array.from({ length: MAX_PHOTOS }, (_, i) => ({ position: i + 1 }))
  const overflow: PhotoSlot[] = []
  for (const p of photos) {
    const idx = p.position - 1
    const slot: PhotoSlot = {
      position: p.position,
      id: p.id,
      status: p.status,
      url: p.url,
      is_main: p.is_main,
    }
    if (idx >= 0 && idx < MAX_PHOTOS) {
      slots[idx] = slot
    } else {
      overflow.push(slot)
    }
  }
  return { slots, overflow }
}

export default function PhotoUploadPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useUserStore((s) => s.me)
  const setHasPhoto = useUserStore((s) => s.setHasPhoto)
  const [slots, setSlots] = useState<PhotoSlot[]>(
    Array.from({ length: MAX_PHOTOS }, (_, i) => ({ position: i + 1 })),
  )
  const [overflow, setOverflow] = useState<PhotoSlot[]>([])
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [activePosition, setActivePosition] = useState<number | null>(null)

  async function loadPhotos() {
    try {
      const photos = await listPhotos()
      const next = slotsFromPhotos(photos)
      setSlots(next.slots)
      setOverflow(next.overflow)
    } catch {
      // non-fatal
    }
  }

  useEffect(() => {
    loadPhotos()
  }, [])

  // Mirrors the in-app "←" button below (browser-history back).
  useTelegramBackButton(() => navigate(-1))

  // Report the "photo" onboarding step only when this page is the mandatory
  // onboarding gate (profile done, no photo yet) — not when an existing user
  // opens it from "Manage Photos" in edit mode.
  useEffect(() => {
    if (me?.profile_complete && !me?.has_photo) {
      reportOnboardingStep('photo').catch(() => {})
    }
  }, [me?.profile_complete, me?.has_photo])

  function handleSlotClick(position: number) {
    const slot = slots[position - 1]
    if (slot.uploading || slot.actioning) return
    setActivePosition(position)
    fileInputRef.current?.click()
  }

  async function handleDelete(id: number, position: number, isOverflow: boolean) {
    const mark = (actioning: boolean) =>
      isOverflow
        ? setOverflow((prev) => prev.map((s) => (s.id === id ? { ...s, actioning } : s)))
        : setSlots((prev) => prev.map((s) => (s.position === position ? { ...s, actioning } : s)))

    mark(true)
    setError(null)
    try {
      await deletePhoto(id)
      await loadPhotos()
    } catch {
      setError(t('photos.deleteFailed'))
      mark(false)
    }
  }

  async function handleSetMain(e: React.MouseEvent, id: number, position: number) {
    e.stopPropagation()
    setSlots((prev) => prev.map((s) => (s.position === position ? { ...s, actioning: true } : s)))
    setError(null)
    try {
      await setMainPhoto(id)
      await loadPhotos()
    } catch {
      setError(t('photos.setMainFailed'))
      setSlots((prev) =>
        prev.map((s) => (s.position === position ? { ...s, actioning: false } : s)),
      )
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || activePosition == null) return
    e.target.value = ''

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t('photos.unsupportedType'))
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t('photos.tooLarge'))
      return
    }

    const localPreview = URL.createObjectURL(file)
    const position = activePosition
    setSlots((prev) =>
      prev.map((s) => (s.position === position ? { ...s, uploading: true, localPreview } : s)),
    )
    setError(null)

    try {
      const compressed = await compressImage(file)

      const presignRes = await authedFetch('/api/v1/photos/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_type: 'image/webp', position }),
      })
      if (!presignRes.ok) throw new Error('Presign failed')
      const { upload_url, r2_key } = (await presignRes.json()) as {
        upload_url: string
        r2_key: string
      }

      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/webp' },
        body: compressed,
      })
      if (!putRes.ok) throw new Error('Upload to R2 failed')

      const confirmRes = await authedFetch('/api/v1/photos/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2_key, position }),
      })
      if (!confirmRes.ok) throw new Error('Confirm failed')
      const { photo_id, status } = (await confirmRes.json()) as {
        photo_id: number
        status: PhotoSlot['status']
      }

      setSlots((prev) =>
        prev.map((s) =>
          s.position === position
            ? { position, id: photo_id, status, localPreview, uploading: false }
            : s,
        ),
      )
      // Update the store so App.tsx routing gate sees has_photo=true immediately
      // without needing a full /me refetch.
      setHasPhoto(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('photos.uploadFailed'))
      setSlots((prev) =>
        prev.map((s) => (s.position === position ? { position, uploading: false } : s)),
      )
    }
  }

  const hasPhotos = slots.some((s) => s.id) || overflow.some((s) => s.id)
  // Only true when a photo is genuinely awaiting review (NSFW moderation or
  // manual admin review enabled). Under the current flags
  // (FEATURES_NSFW_MODERATION=false, FEATURES_AUTO_MODERATION=true) every
  // confirmed photo comes back 'approved' instantly — see handleConfirmPhoto
  // in apps/api/photo_handlers.go — so this stays false and the notice below
  // doesn't claim a review that never happens.
  const hasPendingPhoto = slots.some((s) => s.status === 'pending') || overflow.some((s) => s.status === 'pending')

  function renderStatusOverlay(slot: PhotoSlot) {
    if (slot.actioning) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <span className="text-white text-xs">…</span>
        </div>
      )
    }
    if (slot.uploading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <span className="text-white text-xs">{t('photos.uploading')}</span>
        </div>
      )
    }
    if (slot.status === 'pending') {
      return (
        <div className="absolute bottom-0 left-0 right-0 bg-yellow-500/80 py-0.5 text-center text-xs text-white">
          {t('photos.reviewing')}
        </div>
      )
    }
    if (slot.status === 'rejected') {
      return (
        <div className="absolute bottom-0 left-0 right-0 bg-red-500/90 py-0.5 text-center text-xs text-white">
          {t('photos.rejected')}
        </div>
      )
    }
    return null
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-tg-bg px-4 py-8">
      {/* Header */}
      <div className="mb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-sm font-medium text-tg-hint">
          ←
        </button>
        <h1 className="text-2xl font-semibold text-tg-text">{t('photos.title')}</h1>
      </div>

      <p className="mb-3 text-sm text-tg-hint">{t('photos.hint')}</p>

      {/* Photo grid — 1 large slot (main photo) + 2 small slots. flex-1
          min-h-0 (rather than a fixed aspect ratio) lets the grid fill
          whatever vertical space remains after the header/hint/footer, so
          the whole page always fits in one viewport with no scroll —
          photos already render via object-cover below, so they crop
          gracefully regardless of the resulting cell proportions. The size
          difference itself signals which photo is main, so slot 1 has no
          star/set-main control; slots 2-3 keep it to promote a swap into
          the large slot (see PhotoRepo.PromoteToMain). */}
      <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-2">
        {slots.map((slot, i) => (
          <button
            key={slot.position}
            onClick={() => handleSlotClick(slot.position)}
            disabled={slot.uploading || slot.actioning}
            className={`relative rounded-xl bg-tg-secondary-bg overflow-hidden border-2 border-dashed border-tg-hint/30 focus:outline-none active:opacity-80 disabled:opacity-60 ${
              i === 0 ? 'row-span-2' : ''
            }`}
          >
            {slot.localPreview || slot.url ? (
              <>
                <img
                  src={slot.localPreview ?? slot.url}
                  alt={`Photo ${slot.position}`}
                  className="h-full w-full object-cover"
                />

                {slot.id && !slot.uploading && !slot.actioning && (
                  <div className="absolute inset-0 flex flex-col justify-between p-1">
                    <div className="flex justify-end">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(slot.id!, slot.position, false)
                        }}
                        className="rounded-full bg-black/50 p-1 text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {i === 0 ? (
                      <div className="flex justify-start">
                        <span className="flex items-center gap-1 rounded-full bg-yellow-400/90 px-1.5 py-0.5 text-xs text-white">
                          <Star className="h-3 w-3 fill-current" />
                          {t('photos.main')}
                        </span>
                      </div>
                    ) : (
                      !slot.is_main && (
                        <div className="flex justify-start">
                          <button
                            onClick={(e) => handleSetMain(e, slot.id!, slot.position)}
                            className="rounded-full bg-black/50 p-1 text-yellow-400"
                          >
                            <Star className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )
                    )}
                  </div>
                )}
              </>
            ) : (
              <span className="text-2xl text-tg-hint">+</span>
            )}
            {renderStatusOverlay(slot)}
          </button>
        ))}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Legacy overflow — only ever populated for accounts that uploaded a
          4th photo before the grid was cut to 3. Delete-only: no upload
          slot, no set-main, so there is no way to add back into it. */}
      {overflow.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-tg-hint">{t('photos.legacyLabel')}</p>
          <div className="flex gap-2">
            {overflow.map((slot) => (
              <div
                key={slot.position}
                className="relative h-20 w-20 overflow-hidden rounded-xl bg-tg-secondary-bg"
              >
                {(slot.url || slot.localPreview) && (
                  <img
                    src={slot.localPreview ?? slot.url}
                    alt={`Photo ${slot.position}`}
                    className="h-full w-full object-cover"
                  />
                )}
                {slot.id && !slot.actioning && (
                  <button
                    onClick={() => handleDelete(slot.id!, slot.position, true)}
                    className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                {renderStatusOverlay(slot)}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p className="mt-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

      {hasPendingPhoto && <p className="mt-2 text-xs text-tg-hint">{t('photos.reviewNotice')}</p>}

      <div className="pt-3">
        <button
          onClick={() => navigate('/swipe', { replace: true })}
          disabled={!hasPhotos}
          className={`w-full rounded-xl py-4 font-medium transition-colors ${
            hasPhotos ? 'bg-primary text-white' : 'bg-tg-secondary-bg text-tg-hint opacity-40'
          }`}
        >
          {t('photos.done')}
        </button>
        {!hasPhotos && (
          <p className="mt-2 text-center text-xs text-tg-hint">{t('photos.uploadAtLeastOne')}</p>
        )}
      </div>
    </div>
  )
}
